import { useState } from 'react';
import { SignupAudience, SignupField } from '../types/signup';
import { SUPABASE_KEY, SUPABASE_URL } from '../utils/supabase';

type Status = 'idle' | 'submitting' | 'success' | 'error';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const HONEYPOT_FIELD = 'company_website';

export function useSignupForm(fields: SignupField[], audience: SignupAudience) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<Status>('idle');

  const validate = () => {
    const next: Record<string, string> = {};
    fields.forEach((f) => {
      const v = (values[f.name] ?? '').trim();
      if (f.required && !v) next[f.name] = 'Required';
      else if (f.type === 'email' && v && !EMAIL_RE.test(v)) next[f.name] = 'Enter a valid email';
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleChange = (name: string, value: string) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    // Honeypot: real people never see this field. Bots get a fake success and nothing is stored.
    if ((values[HONEYPOT_FIELD] ?? '').length > 0) {
      setStatus('success');
      return;
    }

    setStatus('submitting');
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/submit_signup`, {
        method: 'POST',
        headers: {
          apikey: SUPABASE_KEY,
          Authorization: `Bearer ${SUPABASE_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          p_audience: audience,
          p_full_name: values.fullName ?? null,
          p_email: values.email ?? null,
          p_city: audience === 'gigs' ? values.city ?? null : null,
          p_company: audience === 'company' ? values.company ?? null : null,
          p_markets: audience === 'company' ? values.markets ?? null : null,
          p_use_case: audience === 'company' ? values.useCase ?? null : null,
          p_referrer: typeof document !== 'undefined' ? document.referrer || null : null,
        }),
      });
      // Success is 204 with an empty body (duplicates too). Never parse JSON here.
      if (!res.ok) throw new Error(`signup failed: ${res.status}`);
      setStatus('success');
    } catch (err) {
      console.error(err);
      setStatus('error');
    }
  };

  const reset = () => {
    setValues({});
    setErrors({});
    setStatus('idle');
  };

  return { values, errors, status, handleChange, handleSubmit, reset };
}
